import React, { FC, forwardRef, useId } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import ContentText from '../atoms/ContentText';
import {
  MdOutlineRadioButtonChecked,
  MdOutlineRadioButtonUnchecked,
} from 'react-icons/md';
import { styled } from 'styled-components';
import Image, { StaticImageData } from 'next/image';

// OVERVIEW
// This atom form component provides styling and accessibility requirements. Validation, event handlers, etc.
// will use react-hook-form via a wrapper component to add interactivity

// TYPES
// This input component is intended to be used for all single line inputs (phone, numbers, text input)
export type InputProps = {
  label: string;
  onChange?: any;
  value: string;
  selected?: boolean;
  disabled?: boolean;
  isRadioGroupButton?: boolean;
  register?: UseFormRegisterReturn<any>;
  image?: StaticImageData;
};
export const RadioButton: FC<InputProps> = forwardRef<
  HTMLInputElement,
  InputProps
>((props, ref) => {
  const id = useId();
  return (
    <>
      <OptionWrapper
        disabled={props.disabled}
        $isRadioGroupButton={props.isRadioGroupButton}
      >
        <input
          ref={ref}
          id={props.value + id}
          type="radio"
          aria-label={props.label}
          value={props.value}
          checked={props.selected}
          {...props.register}
          onChange={(e) => {
            props.onChange(e);
            props.register?.onChange(e);
          }}
          disabled={props.disabled}
        />
        <label
          htmlFor={props.value + id}
          className="block text-sm font-medium text-gray-700"
        >
          {props.selected ? (
            <MdOutlineRadioButtonChecked
              size={20}
              color="var(--color-red, #c12827)"
            ></MdOutlineRadioButtonChecked>
          ) : (
            <MdOutlineRadioButtonUnchecked
              size={20}
            ></MdOutlineRadioButtonUnchecked>
          )}
          <ContentText className="radioCustomLabel">{props.label}</ContentText>
        </label>
      </OptionWrapper>
      {props.image ? (
        <Image src={props.image} width={303} height={184} alt="user-icon" />
      ) : (
        ''
      )}
    </>
  );
});
RadioButton.displayName = 'RadioButton';
const OptionWrapper = styled.div<{
  disabled?: boolean;
  $isRadioGroupButton?: boolean;
}>`
  input {
    display: none;
  }
  label {
    width: fit-content;
    padding: ${(props) => (props.$isRadioGroupButton ? '0' : '8px 0px')};
    display: flex;
    align-items: center;
    gap: ${(props) => (props.$isRadioGroupButton ? '4px' : '12px')};
  }
  ${(props) =>
    props.disabled
      ? 'color: var(--border-disabled);'
      : '&: hover {opacity: 0.9};'};
`;
